import { createContext, useContext, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { dict, type Lang } from './dict';

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  /** Look up a dictionary key; returns a string or JSX node for the current language. */
  t: (key: string) => ReactNode;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

/** Lang is derived from the route: /zh* → zh, everything else → en. */
function langFromPath(pathname: string): Lang {
  return pathname.startsWith('/zh') ? 'zh' : 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const lang = langFromPath(location.pathname);

  const setLang = (next: Lang) => {
    if (next === lang) return;
    // Trailing slash matches the prerendered /zh/ route + canonical URL.
    const extension = location.pathname.replace(/\/$/, '').endsWith('/extension');
    navigate(extension ? (next === 'zh' ? '/zh/extension/' : '/extension/') : (next === 'zh' ? '/zh/' : '/'));
  };

  const t = (key: string): ReactNode => {
    const entry = dict[key];
    if (!entry) {
      if (import.meta.env.DEV) console.warn(`[i18n] missing key: ${key}`);
      return key;
    }
    const value = entry[lang];
    return typeof value === 'function' ? value() : value;
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>{children}</LanguageContext.Provider>
  );
}

export function useLang(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLang must be used within a LanguageProvider');
  return ctx;
}
